import { checkIsAvailableState } from '@/constants/amazonConnect';
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '@/hooks/usePageMetadata';

import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Pill,
  Stack,
  Box,
} from '@gapartners/ui';

type ProfileInfoCardProps = {
  title: string;
  name: string;
  email: string;
  role: string;
  idName: string;
  idValue: string;
  status?: string;
  routingProfileName?: string;
  onAvatarClick?: () => void;
};

const getPillColor = (
  status: string | undefined,
  isAvailable: boolean | undefined
): 'success' | 'error' | 'default' => {
  if (!status) return 'default';
  return isAvailable ? 'success' : 'error';
};

export function ProfileInfoCard({
  title,
  name,
  email,
  role,
  idName,
  idValue,
  status,
  routingProfileName,
  onAvatarClick,
}: ProfileInfoCardProps) {
  const { t } = useTranslation();
  const initial = name?.charAt(0)?.toUpperCase() ?? '?';
  const displayStatus = status ?? t('common.loading');
  const isAvailable = status ? checkIsAvailableState(status) : false;
  const pillColor = getPillColor(status, isAvailable);
  usePageMetadata({
    title: t('pages.myProfile.profileInfo.title'),
    description: t('pages.myProfile.profileInfo.description'),
  });
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          color="text.primary"
          gutterBottom
        >
          {title}
        </Typography>

        <Stack
          direction="row"
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems="flex-start"
        >
          <Avatar
            sx={{
              width: { xs: 48, sm: 56 },
              height: { xs: 48, sm: 56 },
              bgcolor: 'primary.main',
              cursor: onAvatarClick ? 'pointer' : 'default',
            }}
            onClick={onAvatarClick}
          >
            {initial}
          </Avatar>

          <Stack spacing={0.5} flex={1} useFlexGap>
            <Typography variant="body2">
              <Box component="span" color="text.secondary">
                {t('common.name')}:{' '}
              </Box>
              <Box component="span" fontWeight={500}>
                {name}
              </Box>
            </Typography>

            <Typography variant="body2">
              <Box component="span" color="text.secondary">
                {t('common.email')}:{' '}
              </Box>
              <Box component="span">{email}</Box>
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {idName}: {idValue}
            </Typography>

            <Typography variant="body2">
              <Box component="span" color="text.secondary">
                {t('pages.myProfile.role')}:{' '}
              </Box>
              <Box component="span" fontWeight={500}>
                {role || '—'}
              </Box>
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {t('common.status')}:
              </Typography>
              <Pill
                label={displayStatus}
                color={pillColor}
                size="small"
                variant="filled"
              />
            </Stack>

            <Typography variant="body2">
              <Box component="span" color="text.secondary">
                {t('pages.myProfile.routingProfileName')}:{' '}
              </Box>
              <Box component="span" fontWeight={500}>
                {routingProfileName || '—'}
              </Box>
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
